import { useAppStore } from "@/store/use-app-store";
import type { ReactNode } from "react";
import { getCroatianLocative } from "@shared/croatian-locatives";
import { type SiteLang } from "@shared/languages";

export type { SiteLang } from "@shared/languages";

// ─── Translation override engine ────────────────────────────────────────────
// DB-humanized strings override the hardcoded t5() fallbacks automatically.
// New strings discovered at render time are queued and registered to the DB
// so they appear in the admin audit panel and can be humanized in bulk.

// FNV-1a 32-bit hash — stable, fast, deterministic key from English source text
const _fnvCache: Map<string, string> = new Map();
function fnv1a(str: string): string {
  const cached = _fnvCache.get(str);
  if (cached) return cached;
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  const key = "ui." + h.toString(36).padStart(7, "0");
  _fnvCache.set(str, key);
  return key;
}

// Module-level override map: key → humanized text for the currently-loaded language
export const _dbOverrides: Map<string, string> = new Map();

// Registration buffer: new strings discovered during render, flushed async in
// development. Production only reads already-published overrides.
const _regBuffer: Map<string, Record<string, string>> = new Map();
const _registered: Set<string> = new Set(); // keys already sent to server
let _flushTimer: ReturnType<typeof setTimeout> | null = null;
const ENABLE_TRANSLATION_REGISTRATION = import.meta.env.DEV;

function _queueRegistration(key: string, payload: Record<string, string>) {
  if (_registered.has(key) || _regBuffer.has(key)) return;
  _regBuffer.set(key, payload);
  if (!_flushTimer) {
    _flushTimer = setTimeout(_flushRegistrations, 4000);
  }
}

async function _flushRegistrations() {
  _flushTimer = null;
  if (_regBuffer.size === 0) return;
  const batch = Array.from(_regBuffer.entries()).map(([key, v]) => ({ key, ...v }));
  _regBuffer.clear();
  for (const { key } of batch) _registered.add(key);
  try {
    const tokenRes = await fetch("/api/assistant/token");
    const { token } = tokenRes.ok ? await tokenRes.json() : { token: "" };
    // Server hard-caps at 100 strings per request — chunk to avoid 400 errors
    const CHUNK_SIZE = 100;
    for (let i = 0; i < batch.length; i += CHUNK_SIZE) {
      const chunk = batch.slice(i, i + CHUNK_SIZE);
      await fetch("/api/translations/register", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-chat-token": token },
        body: JSON.stringify({ strings: chunk }),
      });
    }
  } catch {
    // Non-critical — silent fail
  }
}

// Called by TranslationProvider when language changes
export function _loadOverrides(lang: string): void {
  if (lang === "en") { _dbOverrides.clear(); return; }
  fetch(`/api/translations/overrides?lang=${lang}`)
    .then(r => r.ok ? r.json() : {})
    .then((map: Record<string, string>) => {
      _dbOverrides.clear();
      for (const [k, v] of Object.entries(map)) _dbOverrides.set(k, v);
    })
    .catch(() => {});
}

// ─── t5 — the core translation function ─────────────────────────────────────

export function t5(lang: string, en: string, de: string, fr: string, it: string, nl?: string, hr?: string, pl?: string): string;
export function t5(lang: string, en: ReactNode, de: ReactNode, fr: ReactNode, it: ReactNode, nl?: ReactNode, hr?: ReactNode, pl?: ReactNode): ReactNode;
export function t5(lang: string, en: string | ReactNode, de: string | ReactNode, fr: string | ReactNode, it: string | ReactNode, nl?: string | ReactNode, hr?: string | ReactNode, pl?: string | ReactNode): string | ReactNode {
  // String path: auto-register for future humanization + check DB override
  if (typeof en === "string") {
    const key = fnv1a(en);

    if (ENABLE_TRANSLATION_REGISTRATION) {
      _queueRegistration(key, {
        en,
        de: (typeof de === "string" ? de : null) ?? en,
        fr: (typeof fr === "string" ? fr : null) ?? en,
        it: (typeof it === "string" ? it : null) ?? en,
        nl: (typeof nl === "string" ? nl : null) ?? en,
        hr: (typeof hr === "string" ? hr : null) ?? en,
        pl: (typeof pl === "string" ? pl : null) ?? en,
      });
    }

    // Return humanized DB version if available (only for non-English)
    if (lang !== "en") {
      const override = _dbOverrides.get(key);
      if (override) return override;
    }
  }

  // Hardcoded fallback (original behaviour)
  if (lang === "de") return de;
  if (lang === "fr") return fr;
  if (lang === "it") return it;
  if (lang === "nl") return nl ?? en;
  if (lang === "hr") return hr ?? en;
  if (lang === "pl") return pl ?? en;
  return en;
}

export function locale5(lang: string): string {
  if (lang === "de") return "de-DE";
  if (lang === "fr") return "fr-FR";
  if (lang === "it") return "it-IT";
  if (lang === "nl") return "nl-NL";
  if (lang === "hr") return "hr-HR";
  if (lang === "pl") return "pl-PL";
  return "en-GB";
}

export function useT() {
  const { language } = useAppStore();
  const lang = language as string;

  return {
    lang,

    home: {
      title: t5(lang, "Smart Croatia – Travel Guide to Beaches, Parks & Cities", "Smart Kroatien – Reiseführer für Strände, Parks & Städte", "Smart Croatie – Guide voyage plages, parcs & villes", "Smart Croazia – Guida viaggio spiagge, parchi e città", "Smart Kroatië – Reisgids stranden, parken & steden", "Smart Hrvatska – Turistički vodič plaže, parkovi i gradovi",
        "Inteligentna Chorwacja – Przewodnik po plażach, parkach i miastach"
      ),
      description: t5(lang, "Plan your Croatia trip with real data. Explore Dubrovnik, Split, Plitvice Lakes, Hvar and more. Compare crowds, weather, and prices.", "Planen Sie Ihre Kroatien-Reise mit echten Daten. Entdecken Sie Dubrovnik, Split, die Plitvicer Seen, Hvar und mehr.", "Planifiez votre voyage en Croatie avec de vraies données. Explorez Dubrovnik, Split, les lacs de Plitvice, Hvar et plus encore.", "Pianifica il tuo viaggio in Croazia con dati reali. Esplora Dubrovnik, Split, i laghi di Plitvice, Hvar e molto altro.", "Plan uw reis naar Kroatië met echte data. Ontdek Dubrovnik, Split, de Plitvicemeren, Hvar en meer.", "Planirajte putovanje u Hrvatsku s pravim podacima. Istražite Dubrovnik, Split, Plitvička jezera, Hvar i još mnogo toga.",
        "Zaplanuj swoją podróż do Chorwacji z prawdziwymi danymi. Odkryj Dubrownik, Split, Jeziora Plitwickie, Hvar i inne. Porównaj tłumy, pogodę i ceny."
      ),
      searchPlaceholder: t5(lang, "Search destinations, parks, regions...", "Reiseziele, Parks, Regionen suchen...", "Rechercher destinations, parcs, régions...", "Cerca destinazioni, parchi, regioni...", "Zoek bestemmingen, parken, regio's...", "Pretraži odredišta, parkove, regije...",
        "Szukaj destynacji, parków, regionów..."
      ),
      destinations: t5(lang, "Destinations", "Reiseziele", "Destinations", "Destinazioni", "Bestemmingen", "Odredišta",
        "Destynacje"
      ),
      regions: t5(lang, "Regions", "Regionen", "Régions", "Regioni", "Regio's", "Regije",
        "Regiony"
      ),
      nationalParks: t5(lang, "National Parks", "Nationalparks", "Parcs nationaux", "Parchi nazionali", "Nationale parken", "Nacionalni parkovi",
        "Parki Narodowe"
      ),
      islands: t5(lang, "Islands", "Inseln", "Îles", "Isole", "Eilanden", "Otoci",
        "Wyspy"
      ),
      featuredRegions: t5(lang, "Featured Regions", "Regionen entdecken", "Régions à découvrir", "Regioni da scoprire", "Uitgelichte regio's", "Istaknute regije",
        "Wyróżnione regiony"
      ),
      exploreRegions: t5(lang, "Explore Croatia's diverse landscapes", "Entdecken Sie Kroatiens vielfältige Landschaften", "Découvrez les paysages variés de la Croatie", "Scopri i paesaggi diversi della Croazia", "Ontdek de gevarieerde landschappen van Kroatië", "Istražite raznolike krajolike Hrvatske",
        "Odkryj różnorodne krajobrazy Chorwacji"
      ),
      exploreMore: t5(lang, "Explore More Croatia", "Mehr aus Kroatien entdecken", "Découvrir plus de Croatie", "Scopri di più della Croazia", "Meer van Kroatië ontdekken", "Istražite više o Hrvatskoj",
        "Odkryj więcej Chorwacji"
      ),
      inDepthGuides: t5(lang, "In-depth guides for every kind of traveller", "Umfassende Reiseführer für jeden Reisenden", "Guides détaillés pour tous les voyageurs", "Guide approfondite per ogni tipo di viaggiatore", "Uitgebreide gidsen voor elke reiziger", "Detaljni vodiči za svaku vrstu putnika",
        "Szczegółowe przewodniki dla każdego rodzaju podróżnika"
      ),
      explore: t5(lang, "Explore", "Erkunden", "Explorer", "Esplora", "Ontdekken", "Istražite",
        "Odkryj"
      ),
      exploreRegion: t5(lang, "Explore Region", "Region erkunden", "Explorer la région", "Esplora la regione", "Regio ontdekken", "Istražite regiju",
        "Odkryj region"
      ),
      nationalParksCard: t5(lang, "National Parks", "Nationalparks", "Parcs nationaux", "Parchi nazionali", "Nationale parken", "Nacionalni parkovi",
        "Parki Narodowe"
      ),
      nationalParksDesc: t5(lang, "Croatia has 8 national parks, from Plitvice Lakes to Kornati archipelago.", "Kroatien hat 8 Nationalparks – von den Plitvicer Seen bis zum Kornaten-Archipel.", "La Croatie compte 8 parcs nationaux, des lacs de Plitvice à l'archipel des Kornati.", "La Croazia ha 8 parchi nazionali, dai laghi di Plitvice all'arcipelago delle Kornati.", "Kroatië heeft 8 nationale parken, van de Plitvicemeren tot de Kornati-archipel.", "Hrvatska ima 8 nacionalnih parkova, od Plitvičkih jezera do Kornatskog arhipelaga.",
        "Chorwacja ma 8 parków narodowych, od Jezior Plitwickich po archipelag Kornati."
      ),
      sailingCard: t5(lang, "Sailing", "Segeln", "Voile", "Vela", "Zeilen", "Jedrenje",
        "Żeglarstwo"
      ),
      sailingDesc: t5(lang, "1,244 islands, 140+ marinas, and the best sailing winds in the Adriatic.", "1.244 Inseln, 140+ Marinas und die besten Segelwinde in der Adria.", "1 244 îles, 140+ marinas et les meilleurs vents de voile en Adriatique.", "1.244 isole, oltre 140 marine e i migliori venti per la vela in Adriatico.", "1.244 eilanden, 140+ jachthavens en de beste zeilvinden in de Adriatische Zee.", "1.244 otoka, 140+ marina i najpovoljniji vjetrovi za jedrenje na Jadranu.",
        "1 244 wyspy, 140+ marin i najlepsze wiatry żeglarskie w Adriatyku."
      ),
      campingCard: t5(lang, "Camping", "Camping", "Camping", "Campeggio", "Kamperen", "Kampiranje",
        "Kemping"
      ),
      campingDesc: t5(lang, "From beachfront pitches to mountain retreats, Croatia has 200+ campsites.", "Von Strandplätzen bis zu Bergunterkünften – Kroatien hat über 200 Campingplätze.", "Des emplacements en bord de mer aux refuges de montagne, la Croatie compte 200+ campings.", "Da piazzole sulla spiaggia a rifugi di montagna, la Croazia ha oltre 200 campeggi.", "Van strandplaatsen tot bergkampen, Kroatië heeft 200+ campings.", "Od kamp mjesta na plaži do planinskih utočišta, Hrvatska ima 200+ kampova.",
        "Od miejsc kempingowych przy plaży po górskie schroniska, Chorwacja ma ponad 200 kempingów."
      ),
      festivalsCard: t5(lang, "Festivals", "Festivals", "Festivals", "Festival", "Festivals", "Festivali",
        "Festiwale"
      ),
      festivalsDesc: t5(lang, "Music, culture and food festivals across Croatia all summer long.", "Musik-, Kultur- und Foodfestivals in ganz Kroatien den ganzen Sommer.", "Festivals de musique, de culture et de gastronomie dans toute la Croatie tout l'été.", "Festival di musica, cultura e gastronomia in tutta la Croazia per tutta l'estate.", "Muziek-, cultuur- en voedingsfestivals door heel Kroatië de hele zomer.", "Glazbeni, kulturni i gastronomski festivali diljem Hrvatske cijelo ljeto.",
        "Festiwale muzyczne, kulturalne i kulinarne w całej Chorwacji przez całe lato."
      ),
      airports: t5(lang, "Croatian International Airports", "Kroatische internationale Flughäfen", "Aéroports internationaux croates", "Aeroporti internazionali croati", "Kroatische internationale luchthavens", "Hrvatski međunarodni aerodromi",
        "Chorwackie międzynarodowe lotniska"
      ),
      airportsDesc: t5(lang, "Live flight data for all 7 commercial airports", "Live-Flugdaten für alle 7 Verkehrsflughäfen", "Données de vol en direct pour les 7 aéroports commerciaux", "Dati di volo in tempo reale per tutti i 7 aeroporti commerciali", "Live vluchtgegevens voor alle 7 commerciële luchthavens", "Podatci o letovima uživo za svih 7 komercijalnih aerodroma",
        "Dane na żywo o lotach dla wszystkich 7 lotnisk komercyjnych"
      ),
      updated: t5(lang, "Updated", "Aktualisiert", "Mis à jour", "Aggiornato", "Bijgewerkt", "Ažurirano",
        "Zaktualizowane"
      ),
      flightsToday: t5(lang, "Flights today", "Flüge heute", "Vols aujourd'hui", "Voli oggi", "Vluchten vandaag", "Letovi danas",
        "Loty dzisiaj"
      ),
      liveRealtime: t5(lang, "· real-time", "· Echtzeit", "· temps réel", "· tempo reale", "· realtime", "· u stvarnom vremenu",
        "· w czasie rzeczywistym"
      ),
      estimated: t5(lang, "· estimated", "· geschätzt", "· estimé", "· stimato", "· geschat", "· procijenjeno",
        "· szacowane"
      ),
      perDay: t5(lang, "PER DAY", "PRO TAG", "PAR JOUR", "AL GIORNO", "PER DAG", "PO DANU",
        "DZIEŃ"
      ),
      liveMap: t5(lang, "Live Map", "Live-Karte", "Carte en direct", "Mappa in tempo reale", "Live kaart", "Karta uživo",
        "Mapa na żywo"
      ),
      airportDataNote: t5(lang, "Airport data powers the AI crowd model", "Flughafendaten stützen das KI-Besuchermodell", "Les données aéroportuaires alimentent le modèle IA de fréquentation", "I dati aeroportuali alimentano il modello IA per le affluenze", "Luchthavendata voedt het AI-bezoekersmodel", "Aerodromski podaci pokreću AI model gužvi",
        "Dane lotnisk zasilają model tłumu AI"
      ),
      paxPerYear: t5(lang, "pax/yr", "Passagiere/Jahr", "pass./an", "pass./anno", "pass./jaar", "put./god.",
        "osób/rok"
      ),
    },

    location: {
      notFound: t5(lang, "Location not found", "Ort nicht gefunden", "Lieu introuvable", "Luogo non trovato", "Locatie niet gevonden", "Mjesto nije pronađeno",
        "Lokalizacja nie znaleziona"
      ),
      about: (name: string, canonicalName?: string) => t5(lang, `About ${name}`, `Über ${name}`, `À propos de ${name}`, `Informazioni su ${name}`, `Over ${name}`, `O ${getCroatianLocative(canonicalName ?? name)}`,
        `O ${name}`
      ),
      exploreRegion: t5(lang, "Explore Region", "Region erkunden", "Explorer la région", "Esplora la regione", "Regio ontdekken", "Istražite regiju",
        "Odkryj region"
      ),
      forecast: t5(lang, "7-Day Forecast", "7-Tage-Vorhersage", "Prévisions 7 jours", "Previsioni 7 giorni", "7-daagse voorspelling", "Prognoza za 7 dana",
        "Prognoza 7-dniowa"
      ),
      tapDay: t5(lang, "Tap any day for detailed conditions", "Auf einen Tag tippen für Details", "Appuyez sur un jour pour les détails", "Tocca un giorno per i dettagli", "Tik op een dag voor details", "Dodirnite dan za detalje",
        "Stuknij w dowolny dzień, aby zobaczyć szczegółowe warunki"
      ),
      conditions: t5(lang, "Conditions", "Wetterlage", "Conditions", "Condizioni", "Omstandigheden", "Uvjeti",
        "Warunki"
      ),
      temperature: t5(lang, "Temperature", "Temperatur", "Température", "Temperatura", "Temperatuur", "Temperatura",
        "Temperatura"
      ),
      uvIndex: t5(lang, "UV Index", "UV-Index", "Indice UV", "Indice UV", "UV-index", "UV indeks",
        "Indeks UV"
      ),
      rainChance: t5(lang, "Rain chance", "Regenwahrscheinlichkeit", "Risque de pluie", "Probabilità di pioggia", "Regenkan", "Mogućnost kiše",
        "Szansa na deszcz"
      ),
      windMax: t5(lang, "Wind (max)", "Wind (max)", "Vent (max)", "Vento (max)", "Wind (max)", "Vjetar (maks)",
        "Wiatr (maks)"
      ),
      sunrise: t5(lang, "Sunrise", "Sonnenaufgang", "Lever du soleil", "Alba", "Zonsopgang", "Izlazak sunca",
        "Wschód słońca"
      ),
      sunset: t5(lang, "Sunset", "Sonnenuntergang", "Coucher du soleil", "Tramonto", "Zonsondergang", "Zalazak sunca",
        "Zachód słońca"
      ),
      liveConditions: (name: string, canonicalName?: string) => t5(lang, `Live Conditions at ${name}`, `Aktuelle Bedingungen bei ${name}`, `Conditions en direct à ${name}`, `Condizioni in tempo reale a ${name}`, `Live omstandigheden bij ${name}`, `Uvjeti uživo u ${getCroatianLocative(canonicalName ?? name)}`,
        `Warunki na żywo w ${name}`
      ),
      seaTemp: t5(lang, "Sea Temp", "Meerestemperatur", "Temp. mer", "Temp. mare", "Zeetemp.", "Temp. mora",
        "Temperatura morza"
      ),
      sea: t5(lang, "Sea", "Meer", "Mer", "Mare", "Zee", "More",
        "Morze"
      ),
      wind: t5(lang, "Wind", "Wind", "Vent", "Vento", "Wind", "Vjetar",
        "Wiatr"
      ),
      humidity: t5(lang, "Humidity", "Luftfeuchtigkeit", "Humidité", "Umidità", "Vochtigheid", "Vlažnost",
        "Wilgotność"
      ),
      snowDepth: t5(lang, "Snow Depth", "Schneehöhe", "Hauteur de neige", "Altezza neve", "Sneeuwdiepte", "Dubina snijega",
        "Głębokość śniegu"
      ),
      uv: t5(lang, "UV", "UV", "UV", "UV", "UV", "UV",
        "UV"
      ),
      realtimeData: t5(lang, "Real-time data from Open-Meteo.com", "Echtzeit-Daten von Open-Meteo.com", "Données en temps réel d'Open-Meteo.com", "Dati in tempo reale da Open-Meteo.com", "Realtime data van Open-Meteo.com", "Podaci u stvarnom vremenu s Open-Meteo.com",
        "Dane w czasie rzeczywistym z Open-Meteo.com"
      ),
      bestTimeToday: t5(lang, "Best Time to Visit Today", "Beste Besuchszeit heute", "Meilleur moment pour visiter aujourd'hui", "Miglior momento per visitare oggi", "Beste bezoektijd vandaag", "Najbolje vrijeme za posjet danas",
        "Najlepszy czas na wizytę dzisiaj"
      ),
      indoorActivities: t5(lang, "Consider indoor activities or check back for updates.", "Erwägen Sie Aktivitäten in Innenräumen oder schauen Sie später wieder vorbei.", "Envisagez des activités en intérieur ou revenez plus tard.", "Considera attività al chiuso o torna a controllare più tardi.", "Overweeg binnenactiviteiten of kom later terug.", "Razmotrite aktivnosti u zatvorenom prostoru ili provjerite kasnije.",
        "Rozważ zajęcia w pomieszczeniach lub sprawdź ponownie aktualizacje."
      ),
      calculatedFrom: t5(lang, "Calculated from real weather, UV & wind data", "Berechnet aus Echtzeit-Wetter-, UV- und Winddaten", "Calculé à partir de données météo, UV et vent réels", "Calcolato da dati meteo, UV e vento reali", "Berekend op basis van echte weer-, UV- en winddata", "Izračunato iz stvarnih podataka o vremenu, UV i vjetru",
        "Obliczone na podstawie rzeczywistych danych pogodowych, UV i wiatru"
      ),
      tourActivities: t5(lang, "Tours & Activities", "Touren & Aktivitäten", "Tours et activités", "Tour e attività", "Tours & activiteiten", "Ture i aktivnosti",
        "Wycieczki i Atrakcje"
      ),
      tourDesc: (name: string, canonicalName?: string) => t5(lang, `Book guided tours, day trips, and experiences in ${name}.`, `Buchen Sie geführte Touren, Tagesausflüge und Erlebnisse in ${name}.`, `Réservez des visites guidées, excursions et expériences à ${name}.`, `Prenota tour guidati, escursioni e esperienze a ${name}.`, `Boek rondleidingen, dagtochten en ervaringen in ${name}.`, `Rezervirajte vođene ture, izlete i iskustva u ${getCroatianLocative(canonicalName ?? name)}.`,
        `Zarezerwuj wycieczki z przewodnikiem, wyjazdy jednodniowe i doświadczenia w ${name}.`
      ),
      findExperiences: t5(lang, "Find Experiences on GetYourGuide", "Erlebnisse auf GetYourGuide finden", "Trouver des expériences sur GetYourGuide", "Trova esperienze su GetYourGuide", "Vind ervaringen op GetYourGuide", "Pronađite iskustva na GetYourGuide",
        "Znajdź doświadczenia na GetYourGuide"
      ),
      carRental: t5(lang, "Car Rental", "Mietwagen", "Location de voiture", "Noleggio auto", "Autoverhuur", "Najam automobila",
        "Wynajem samochodów"
      ),
      carRentalDesc: t5(lang, "Compare car hire prices and book online for your Croatia trip.", "Vergleichen Sie Mietwagenpreise und buchen Sie online für Ihre Kroatien-Reise.", "Comparez les prix de location de voiture et réservez en ligne pour votre voyage en Croatie.", "Confronta i prezzi di noleggio auto e prenota online per il tuo viaggio in Croazia.", "Vergelijk autohuurprijzen en boek online voor uw reis naar Kroatië.", "Usporedite cijene najma automobila i rezervirajte online za putovanje po Hrvatskoj.",
        "Porównaj ceny wynajmu samochodów i zarezerwuj online na swoją podróż do Chorwacji."
      ),
      searchCars: t5(lang, "Search Cars on EconomyBookings", "Mietwagen auf EconomyBookings finden", "Chercher des voitures sur EconomyBookings", "Cerca auto su EconomyBookings", "Zoek auto's op EconomyBookings", "Tražite automobile na EconomyBookings",
        "Szukaj samochodów na EconomyBookings"
      ),
      sunSchedule: t5(lang, "Sun Schedule", "Sonnenzeiten", "Horaires du soleil", "Orari del sole", "Zon tijden", "Raspored sunca",
        "Harmonogram Słońca"
      ),
      sunriseLabel: t5(lang, "Sunrise", "Sonnenaufgang", "Lever du soleil", "Alba", "Zonsopgang", "Izlazak sunca",
        "Wschód słońca"
      ),
      sunsetLabel: t5(lang, "Sunset", "Sonnenuntergang", "Coucher du soleil", "Tramonto", "Zonsondergang", "Zalazak sunca",
        "Zachód słońca"
      ),
      goldenHour: t5(lang, "Golden Hour", "Goldene Stunde", "Heure dorée", "Ora d'oro", "Gouden uur", "Zlatni sat",
        "Złota Godzina"
      ),
      dayLength: t5(lang, "Day Length", "Tageslänge", "Durée du jour", "Durata del giorno", "Daglengte", "Duljina dana",
        "Długość Dnia"
      ),
      plan: t5(lang, "Plan", "Merken", "Planifier", "Pianifica", "Plannen", "Planirati",
        "Plan"
      ),
      added: t5(lang, "Added", "Hinzugefügt", "Ajouté", "Aggiunto", "Toegevoegd", "Dodano",
        "Dodano"
      ),
      removedItinerary: t5(lang, "Removed from itinerary", "Aus Reiseroute entfernt", "Retiré de l'itinéraire", "Rimosso dall'itinerario", "Verwijderd uit reisplan", "Uklonjen iz plana",
        "Usunięto z planu podróży"
      ),
      addedItinerary: t5(lang, "Added to itinerary", "Zur Reiseroute hinzugefügt", "Ajouté à l'itinéraire", "Aggiunto all'itinerario", "Toegevoegd aan reisplan", "Dodano u plan putovanja",
        "Dodano do planu podróży"
      ),
      shareTitle: (name: string) => t5(lang, `${name} – Smart Croatia Travel Guide`, `${name} – Smart Kroatien Reiseführer`, `${name} – Smart Croatie Guide voyage`, `${name} – Smart Croazia Guida viaggio`, `${name} – Smart Kroatië Reisgids`, `${name} – Smart Hrvatska Turistički vodič`,
        `${name} – Inteligentny Przewodnik po Chorwacji`
      ),
      shareText: (name: string) => t5(lang, `Discover ${name}, Croatia`, `Entdecke ${name}, Kroatien`, `Découvrez ${name}, Croatie`, `Scopri ${name}, Croazia`, `Ontdek ${name}, Kroatië`, `Otkrijte ${name}, Hrvatska`,
        `Odkryj ${name}, Chorwacja`
      ),

      costGuide: t5(lang, "Travel Cost Guide 2026", "Reisekostenführer 2026", "Guide des coûts de voyage 2026", "Guida ai costi di viaggio 2026", "Reiskostengids 2026", "Vodič za troškove putovanja 2026",
        "Przewodnik po kosztach podróży 2026"
      ),
      live: t5(lang, "Live", "Live", "Direct", "Live", "Live", "Uživo",
        "Na żywo"
      ),
      budgetLabel: t5(lang, "Budget", "Budget", "Budget", "Budget", "Budget", "Budžet",
        "Budżet"
      ),
      selfCatering: (cost: number) => t5(lang, `Self-catering ~€${cost}/day`, `Selbstverpflegung ~€${cost}/Tag`, `Auberge ~€${cost}/jour`, `Autogestione ~€${cost}/giorno`, `Zelfverzorgd ~€${cost}/dag`, `Vlastita prehrana ~€${cost}/dan`,
        `Samodzielne wyżywienie ~€${cost}/dzień`
      ),
      seePriceBreakdown: t5(lang, "See price breakdown →", "Preisübersicht ansehen →", "Voir le détail des prix →", "Vedi dettaglio prezzi →", "Zie prijsoverzicht →", "Pogledajte pregled cijena →",
        "Zobacz szczegóły cen →"
      ),
      marketPrices: t5(lang, "Market prices", "Marktpreise", "Prix du marché", "Prezzi di mercato", "Marktprijzen", "Tržišne cijene",
        "Ceny rynkowe"
      ),
      reviewSubmitted: t5(lang, "Review submitted", "Bewertung gesendet", "Avis soumis", "Recensione inviata", "Beoordeling ingediend", "Recenzija poslana",
        "Recenzja wysłana"
      ),
      thankYou: t5(lang, "Thank you for sharing your experience!", "Vielen Dank für das Teilen Ihrer Erfahrung!", "Merci de partager votre expérience!", "Grazie per aver condiviso la tua esperienza!", "Bedankt voor het delen van uw ervaring!", "Hvala što ste podijelili iskustvo!",
        "Dziękujemy za podzielenie się swoim doświadczeniem!"
      ),
      failedReview: t5(lang, "Failed to submit review", "Bewertung konnte nicht gesendet werden", "Échec de l'envoi de l'avis", "Invio recensione fallito", "Beoordeling indienen mislukt", "Slanje recenzije nije uspjelo",
        "Nie udało się wysłać recenzji"
      ),
      error: t5(lang, "Error", "Fehler", "Erreur", "Errore", "Fout", "Greška",
        "Błąd"
      ),
      signInRequired: t5(lang, "Sign in required", "Anmeldung erforderlich", "Connexion requise", "Accesso richiesto", "Inloggen vereist", "Prijava je obavezna",
        "Wymagana jest rejestracja"
      ),
      pleaseSignIn: t5(lang, "Please sign in to leave a review.", "Bitte melden Sie sich an, um eine Bewertung abzugeben.", "Veuillez vous connecter pour laisser un avis.", "Accedi per lasciare una recensione.", "Log in om een beoordeling achter te laten.", "Molimo prijavite se da ostavite recenziju.",
        "Proszę się zalogować, aby zostawić recenzję."
      ),
      ratingRequired: t5(lang, "Rating required", "Bewertung erforderlich", "Note requise", "Valutazione richiesta", "Beoordeling vereist", "Ocjena je obavezna",
        "Wymagana ocena"
      ),
      pleaseRate: t5(lang, "Please select a star rating.", "Bitte wählen Sie eine Sternebewertung aus.", "Veuillez sélectionner une note en étoiles.", "Seleziona una valutazione a stelle.", "Selecteer een sterrenbeoordeling.", "Molimo odaberite zvjezdičnu ocjenu.",
        "Proszę wybrać ocenę w gwiazdkach."
      ),
      commentTooShort: t5(lang, "Comment too short", "Kommentar zu kurz", "Commentaire trop court", "Commento troppo breve", "Reactie te kort", "Komentar je prekratak",
        "Komentarz za krótki"
      ),
      writeTenChars: t5(lang, "Please write at least 10 characters.", "Bitte schreiben Sie mindestens 10 Zeichen.", "Veuillez écrire au moins 10 caractères.", "Scrivi almeno 10 caratteri.", "Schrijf minimaal 10 tekens.", "Molimo napišite najmanje 10 znakova.",
        "Proszę napisać co najmniej 10 znaków."
      ),
      cruiseShips: t5(lang, "Cruise Ships", "Kreuzfahrtschiffe", "Paquebots de croisière", "Navi da crociera", "Cruiseschepen", "Brodovi za krstarenje",
        "Statki wycieczkowe"
      ),
      inPortToday: t5(lang, "In Port Today", "Heute in Port", "Au port aujourd'hui", "In porto oggi", "Vandaag in haven", "U luci danas",
        "W porcie dzisiaj"
      ),
      noShips: t5(lang, "No cruise ships in port today.", "Heute keine Kreuzfahrtschiffe im Hafen.", "Aucun paquebot au port aujourd'hui.", "Nessuna nave da crociera in porto oggi.", "Vandaag geen cruiseschepen in de haven.", "Danas nema brodova za krstarenje u luci.",
        "Brak statków wycieczkowych w porcie dzisiaj."
      ),
      upcomingArrivals: t5(lang, "Upcoming Calls", "Nächste Ankünfte", "Prochaines escales", "Prossimi arrivi", "Aankomende aanmeldingen", "Predstojeći dolasci",
        "Nadchodzące wizyty"
      ),
      ships: (n: number) => t5(lang, `${n} ships`, `${n} Schiffe`, `${n} navires`, `${n} navi`, `${n} schepen`, `${n} brodova`,
        `${n} statków`
      ),
      dayLabel: t5(lang, "Today", "Heute", "Aujourd'hui", "Oggi", "Vandaag", "Danas",
        "Dziś"
      ),
    },

    parks: {
      title: t5(lang, "Croatia National Parks – Plitvice, Krka, Kornati & More", "Nationalparks in Kroatien – Plitvice, Krka, Kornati & mehr", "Parcs nationaux en Croatie – Plitvice, Krka, Kornati et plus", "Parchi nazionali in Croazia – Plitvice, Krka, Kornati e altri", "Nationale parken in Kroatië – Plitvice, Krka, Kornati & meer", "Nacionalni parkovi u Hrvatskoj – Plitvice, Krka, Kornati i više",
        "Chorwackie Parki Narodowe – Plitvice, Krka, Kornati i inne"
      ),
      description: t5(lang, "Explore Croatia's 8 national parks with entrance fees, best times to visit, and practical travel tips.", "Entdecken Sie Kroatiens 8 Nationalparks mit Eintrittspreisen, besten Besuchszeiten und praktischen Reisetipps.", "Explorez les 8 parcs nationaux de Croatie avec tarifs d'entrée, meilleures périodes et conseils pratiques.", "Esplora gli 8 parchi nazionali della Croazia con prezzi d'ingresso, periodi migliori e consigli pratici.", "Ontdek de 8 nationale parken van Kroatië met toegangsprijzen, beste bezoektijden en praktische reisgidsen.", "Istražite 8 nacionalnih parkova Hrvatske s cijenama ulaznica, najboljim vremenima posjeta i praktičnim savjetima.",
        "Odkryj 8 parków narodowych Chorwacji z opłatami za wstęp, najlepszymi porami do odwiedzenia i praktycznymi wskazówkami podróżniczymi."
      ),
      established: (year: number) => t5(lang, `Est. ${year}`, `Seit ${year}`, `Créé en ${year}`, `Fondato nel ${year}`, `Opgericht ${year}`, `Osnovan ${year}`,
        `Szac. ${year}`
      ),
    },

    region: {
      notFound: t5(lang, "Region not found", "Region nicht gefunden", "Région introuvable", "Regione non trovata", "Regio niet gevonden", "Regija nije pronađena",
        "Region nie znaleziony"
      ),
      regionLabel: t5(lang, "Region", "Region", "Région", "Regione", "Regio", "Regija",
        "Region"
      ),
      destinationsIn: (name: string) => t5(lang, `Destinations in ${name}`, `Reiseziele in ${name}`, `Destinations dans ${name}`, `Destinazioni in ${name}`, `Bestemmingen in ${name}`, `Odredišta u ${getCroatianLocative(name)}`,
        `Destynacje w ${name}`
      ),
      about: (name: string) => t5(lang, `About ${name} region`, `Über die Region ${name}`, `À propos de la région ${name}`, `Informazioni sulla regione ${name}`, `Over de regio ${name}`, `O regiji ${getCroatianLocative(name)}`,
        `O regionie ${name}`
      ),
      ad: t5(lang, "Ad", "Anzeige", "Pub", "Annuncio", "Advertentie", "Oglas",
        "Reklama"
      ),
      bookExperiences: t5(lang, "Book unique experiences", "Einzigartige Erlebnisse buchen", "Réservez des expériences uniques", "Prenota esperienze uniche", "Boek unieke ervaringen", "Rezervirajte jedinstvena iskustva",
        "Zarezerwuj unikalne doświadczenia"
      ),
      bookExperiencesBtn: t5(lang, "Find Experiences on GetYourGuide", "Erlebnisse auf GetYourGuide finden", "Trouver des expériences sur GetYourGuide", "Trova esperienze su GetYourGuide", "Vind ervaringen op GetYourGuide", "Pronađite iskustva na GetYourGuide",
        "Znajdź doświadczenia na GetYourGuide"
      ),
      bookCar: t5(lang, "Book a rental car", "Mietwagen buchen", "Louer une voiture", "Noleggia un'auto", "Huur een auto", "Rezervirajte najam automobila",
        "Zarezerwuj samochód na wynajem"
      ),
      bookCarDesc: t5(lang, "Compare prices and book your Croatia hire car online.", "Vergleichen Sie Preise und buchen Sie Ihren Kroatien-Mietwagen online.", "Comparez les prix et réservez votre voiture de location en Croatie.", "Confronta i prezzi e prenota la tua auto a noleggio in Croazia.", "Vergelijk prijzen en boek uw huurwagen in Kroatië online.", "Usporedite cijene i rezervirajte najam automobila u Hrvatskoj online.",
        "Porównaj ceny i zarezerwuj samochód do wynajęcia w Chorwacji online."
      ),
      bookCarBtn: t5(lang, "Search Cars on EconomyBookings", "Mietwagen auf EconomyBookings finden", "Chercher des voitures sur EconomyBookings", "Cerca auto su EconomyBookings", "Zoek auto's op EconomyBookings", "Tražite automobile na EconomyBookings",
        "Szukaj samochodów na EconomyBookings"
      ),
    },

    camping: {
      title: t5(lang, "Best Campsites in Croatia 2026 – Beach, Forest & Island Camping", "Beste Campingplätze in Kroatien 2026 – Strand, Wald & Insel", "Meilleurs campings en Croatie 2026 – plage, forêt et île", "Migliori campeggi in Croazia 2026 – spiaggia, foresta e isola", "Beste campings in Kroatië 2026 – strand, bos en eiland", "Najbolji kampovi u Hrvatskoj 2026 – plaža, šuma i otok",
        "Najlepsze pola namiotowe w Chorwacji 2026 – Camping na plaży, w lesie i na wyspach"
      ),
      description: t5(lang, "Discover the best Croatian campsites with real prices, locations and amenities. From Istria to Dalmatia.", "Entdecken Sie die besten kroatischen Campingplätze mit echten Preisen, Standorten und Einrichtungen.", "Découvrez les meilleurs campings croates avec prix réels, emplacements et équipements.", "Scopri i migliori campeggi croati con prezzi reali, posizioni e servizi.", "Ontdek de beste Kroatische campings met echte prijzen, locaties en faciliteiten.", "Otkrijte najbolje hrvatske kampove s pravim cijenama, lokacijama i sadržajima. Od Istre do Dalmacije.",
        "Odkryj najlepsze chorwackie pola namiotowe z rzeczywistymi cenami, lokalizacjami i udogodnieniami. Od Istrii po Dalmację."
      ),
      heading: t5(lang, "Camping in Croatia", "Camping in Kroatien", "Camping en Croatie", "Campeggio in Croazia", "Kamperen in Kroatië", "Kampiranje u Hrvatskoj",
        "Camping w Chorwacji"
      ),
      breadcrumb: t5(lang, "Croatia Camping Guide", "Kroatien Camping-Reiseführer", "Guide camping Croatie", "Guida campeggio Croazia", "Kroatië Kampeergids", "Vodič za kampiranje u Hrvatskoj",
        "Przewodnik po campingach w Chorwacji"
      ),
      all: t5(lang, "All", "Alle", "Tous", "Tutti", "Alle", "Svi",
        "Wszystko"
      ),
      beach: t5(lang, "Beach", "Strand", "Plage", "Spiaggia", "Strand", "Plaža",
        "Plaża"
      ),
      forest: t5(lang, "Forest", "Wald", "Forêt", "Foresta", "Bos", "Šuma",
        "Las"
      ),
      island: t5(lang, "Island", "Insel", "Île", "Isola", "Eiland", "Otok",
        "Wyspa"
      ),
      mountain: t5(lang, "Mountain", "Berg", "Montagne", "Montagna", "Berg", "Planina",
        "Góra"
      ),
      river: t5(lang, "River", "Fluss", "Rivière", "Fiume", "Rivier", "Rijeka",
        "Rzeka"
      ),
      type: t5(lang, "Type:", "Typ:", "Type:", "Tipo:", "Type:", "Vrsta:",
        "Typ:"
      ),
      regionLabel: t5(lang, "Region:", "Region:", "Région:", "Regione:", "Regio:", "Regija:",
        "Region:"
      ),
      campsitesFound: t5(lang, "campsites found", "Campingplätze gefunden", "campings trouvés", "campeggi trovati", "campings gevonden", "pronađeno kampova",
        "znalezione pola namiotowe"
      ),
      book: t5(lang, "Book", "Buchen", "Réserver", "Prenota", "Boeken", "Rezerviraj",
        "Rezerwuj"
      ),
      more: t5(lang, "more", "weitere", "de plus", "in più", "meer", "više",
        "więcej"
      ),
      importantTips: t5(lang, "Essential Camping Tips for Croatia", "Wichtige Camping-Tipps für Kroatien", "Conseils essentiels pour camper en Croatie", "Consigli essenziali per campeggiare in Croazia", "Essentiële kampeertips voor Kroatië", "Bitni savjeti za kampiranje u Hrvatskoj",
        "Podstawowe porady dotyczące campingu w Chorwacji"
      ),
      planSailing: t5(lang, "Planning a sailing trip too?", "Auch eine Segelreise geplant?", "Vous planifiez aussi un voyage en voilier?", "Stai pianificando anche un viaggio in barca?", "Plant u ook een zeilreis?", "Planirate i jedriličarsko putovanje?",
        "Planujesz również rejs?"
      ),
      exploreSailing: t5(lang, "Explore Sailing Guide", "Segelführer erkunden", "Explorer le guide de voile", "Esplora la guida alla vela", "Verken de zeilgids", "Istražite vodič za jedrenje",
        "Odkryj przewodnik po żeglarstwie"
      ),
    },

    sailing: {
      title: t5(lang, "Sailing Croatia 2026 – Routes, Marinas & Charter Guide", "Kroatien Segeln 2026 – Routen, Marinas & Charter-Guide", "Voile en Croatie 2026 – Routes, marinas et guide charter", "Vela in Croazia 2026 – Rotte, marine e guida charter", "Zeilen Kroatië 2026 – Routes, jachthavens & chartergids", "Jedrenje u Hrvatskoj 2026 – Rute, marine i vodič za charter",
        "Żeglarstwo w Chorwacji 2026 – Trasy, Mariny i Przewodnik po czarterze"
      ),
      description: t5(lang, "Plan your Croatia sailing holiday with marina guides, route maps and charter options.", "Planen Sie Ihren Kroatien-Segelurlaub mit Marineführern, Routenkarten und Charter-Optionen.", "Planifiez vos vacances à voile en Croatie avec guides de marinas, cartes de routes et options charter.", "Pianifica le tue vacanze in barca a vela in Croazia con guide marine, mappe di rotte e opzioni charter.", "Plan uw zeilvakantie in Kroatië met jachthavengidsen, routekaarten en charteropties.", "Planirajte jedriličarski odmor u Hrvatskoj s vodičima za marine, kartama ruta i charter opcijama.",
        "Zaplanować wakacje żeglarskie w Chorwacji z przewodnikami po marinach, mapami tras i opcjami czarteru."
      ),
      heading: t5(lang, "Sailing in Croatia", "Segeln in Kroatien", "Voile en Croatie", "Vela in Croazia", "Zeilen in Kroatië", "Jedrenje u Hrvatskoj",
        "Żeglarstwo w Chorwacji"
      ),
      breadcrumb: t5(lang, "Croatia Sailing Guide", "Kroatien Segelführer", "Guide de voile Croatie", "Guida vela Croazia", "Kroatië Zeilgids", "Vodič za jedrenje u Hrvatskoj",
        "Przewodnik po żeglarstwie w Chorwacji"
      ),
      islandsLabel: t5(lang, "Islands", "Inseln", "Îles", "Isole", "Eilanden", "Otoci",
        "Wyspy"
      ),
      marinasLabel: t5(lang, "Marinas", "Marinas", "Marinas", "Marine", "Jachthavens", "Marine",
        "Mariny"
      ),
      coastlineLabel: t5(lang, "Coastline", "Küstenlinie", "Littoral", "Litorale", "Kustlijn", "Obala",
        "Wybrzeże"
      ),
      routeHighlights: t5(lang, "Route highlights", "Routen-Highlights", "Points forts de la route", "Punti salienti del percorso", "Route hoogtepunten", "Atrakcije rute",
        "Najważniejsze punkty trasy"
      ),
      bookTour: t5(lang, "Book a Sailing Tour", "Sailing-Tour buchen", "Réserver un tour en voilier", "Prenota un tour in barca a vela", "Boek een zeilreis", "Rezervirajte jedriličarsku turu",
        "Zarezerwuj wycieczkę żeglarską"
      ),
      bestTimeSail: t5(lang, "Best Time to Sail", "Beste Zeit zum Segeln", "Meilleure période pour naviguer", "Periodo migliore per navigare", "Beste tijd om te zeilen", "Najbolje vrijeme za jedrenje",
        "Najlepszy czas na żeglowanie"
      ),
      keyMarinas: t5(lang, "Key Marinas", "Wichtige Marinas", "Marinas principales", "Marine principali", "Belangrijkste jachthavens", "Ključne marine",
        "Kluczowe mariny"
      ),
      berthsLabel: t5(lang, "berths", "Liegeplätze", "anneaux", "ormeggi", "ligplaatsen", "vezova",
        "miejsca do cumowania"
      ),
      depthLabel: t5(lang, "Depth", "Tiefe", "Profondeur", "Profondità", "Diepte", "Dubina",
        "Głębokość"
      ),
      officialWebsite: t5(lang, "Official Website", "Offizielle Website", "Site officiel", "Sito ufficiale", "Officiële website", "Službena web stranica",
        "Oficjalna strona internetowa"
      ),
      licenceReqs: t5(lang, "Licence Requirements", "Lizenzanforderungen", "Exigences de licence", "Requisiti di licenza", "Licentievereisten", "Uvjeti dozvole",
        "Wymagania dotyczące licencji"
      ),
      charterBoat: t5(lang, "Chartering a Boat", "Ein Boot chartern", "Affréter un bateau", "Noleggiare una barca", "Een boot charteren", "Charter broda",
        "Czarterowanie łodzi"
      ),
      bareboadTitle: t5(lang, "Bareboat Charter", "Bareboat-Charter", "Charter bareboat", "Charter bareboat", "Bareboat charter", "Bareboat charter",
        "Czarter bez załogi"
      ),
      skipperedTitle: t5(lang, "Skippered Charter", "Skipper-Charter", "Charter avec skipper", "Charter con skipper", "Charter met schipper", "Charter sa skipperom",
        "Czarter z kapitanem"
      ),
      flotillaTitle: t5(lang, "Flotilla Sailing", "Flottillen-Segeln", "Voile en flottille", "Vela in flottiglia", "Vlootzeil", "Flotilno jedrenje",
        "Żeglarstwo flotyllowe"
      ),
      bareboadPrice: t5(lang, "€700–2,500/week", "€700–2.500/Woche", "€700–2 500/semaine", "€700–2.500/settimana", "€700–2.500/week", "€700–2.500/tjedan",
        "€700–2,500/tydzień"
      ),
      skipperedPrice: t5(lang, "€1,200–4,000/week", "€1.200–4.000/Woche", "€1 200–4 000/semaine", "€1.200–4.000/settimana", "€1.200–4.000/week", "€1.200–4.000/tjedan",
        "€1,200–4,000/tydzień"
      ),
      flotillaPrice: t5(lang, "€900–2,500/week", "€900–2.500/Woche", "€900–2 500/semaine", "€900–2.500/settimana", "€900–2.500/week", "€900–2.500/tjedan",
        "€900–2,500/tydzień"
      ),
      lookingLand: t5(lang, "Looking for somewhere to stay on land?", "Suchen Sie eine Unterkunft an Land?", "Vous cherchez un hébergement à terre?", "Cerchi un posto dove soggiornare a terra?", "Op zoek naar verblijf aan wal?", "Tražite smještaj na kopnu?",
        "Szukasz miejsca na lądzie?"
      ),
      campingGuide: t5(lang, "Camping Guide", "Camping-Führer", "Guide camping", "Guida campeggio", "Kampeergids", "Vodič za kampiranje",
        "Przewodnik po campingach"
      ),
      nationalParks: t5(lang, "National Parks", "Nationalparks", "Parcs nationaux", "Parchi nazionali", "Nationale parken", "Nacionalni parkovi",
        "Parki Narodowe"
      ),
    },

    festivals: {
      title: (year: number) => t5(lang, `Croatia Festivals ${year} – Music, Culture & Food Events`, `Kroatien Festivals ${year} – Musik, Kultur & Food-Events`, `Festivals en Croatie ${year} – musique, culture et gastronomie`, `Festival in Croazia ${year} – musica, cultura e gastronomia`, `Festivals in Kroatië ${year} – muziek, cultuur en eten`, `Festivali u Hrvatskoj ${year} – glazba, kultura i hrana`,
        `Festiwale w Chorwacji ${year} – Wydarzenia muzyczne, kulturalne i kulinarne`
      ),
      description: (year: number) => t5(lang, `Complete guide to Croatia's best festivals in ${year}. Find dates, locations and crowd predictions.`, `Vollständiger Leitfaden zu Kroatiens besten Festivals ${year}. Termine, Orte und Besucherprognosen.`, `Guide complet des meilleurs festivals de Croatie en ${year}. Dates, lieux et prévisions de fréquentation.`, `Guida completa ai migliori festival della Croazia nel ${year}. Date, luoghi e previsioni di affluenza.`, `Volledige gids voor de beste festivals van Kroatië in ${year}. Data, locaties en bezoekersprognoses.`, `Potpuni vodič za najbolje festivale u Hrvatskoj u ${year}. Pronađite datume, lokacije i prognoze gužvi.`,
        `Kompletny przewodnik po najlepszych festiwalach w Chorwacji w ${year}. Znajdź daty, lokalizacje i prognozy frekwencji.`
      ),
      heading: (year: number) => t5(lang, `Croatia Festivals ${year}`, `Kroatische Festivals ${year}`, `Festivals croates ${year}`, `Festival croati ${year}`, `Kroatische festivals ${year}`, `Festivali u Hrvatskoj ${year}`,
        `Festiwale w Chorwacji ${year}`
      ),
      breadcrumb: (year: number) => t5(lang, `Croatia Festivals ${year}`, `Kroatien Festivals ${year}`, `Festivals Croatie ${year}`, `Festival Croazia ${year}`, `Kroatië Festivals ${year}`, `Festivali u Hrvatskoj ${year}`,
        `Festiwale w Chorwacji ${year}`
      ),
      loading: t5(lang, "Loading festivals...", "Festivals werden geladen...", "Chargement des festivals...", "Caricamento festival...", "Festivals laden...", "Učitavam festivale...",
        "Ładowanie festiwali..."
      ),
      filterMonth: t5(lang, "Filter by month", "Nach Monat filtern", "Filtrer par mois", "Filtra per mese", "Filteren op maand", "Filtriraj po mjesecu",
        "Filtruj według miesiąca"
      ),
      filterType: t5(lang, "Filter by type", "Nach Typ filtern", "Filtrer par type", "Filtra per tipo", "Filteren op type", "Filtriraj po vrsti",
        "Filtruj według typu"
      ),
      noFestivals: t5(lang, "No festivals match these filters", "Keine Festivals entsprechen diesen Filtern", "Aucun festival ne correspond à ces filtres", "Nessun festival corrisponde a questi filtri", "Geen festivals komen overeen met deze filters", "Nema festivala koji odgovaraju ovim filterima",
        "Brak festiwali pasujących do tych filtrów"
      ),
      tryAnother: t5(lang, "Try a different month or type", "Versuchen Sie einen anderen Monat oder Typ", "Essayez un autre mois ou type", "Prova un altro mese o tipo", "Probeer een andere maand of type", "Pokušajte s drugim mjesecom ili vrstom",
        "Spróbuj innego miesiąca lub typu"
      ),
      estimated: t5(lang, "Est.", "Geschätzt", "Est.", "Stim.", "Gest.", "Proc.",
        "Szac."
      ),
      estimatedTitle: t5(lang, "Dates are estimated based on prior years. Official dates not yet confirmed.", "Die Daten werden auf der Grundlage der Vorjahre geschätzt. Die offiziellen Termine sind noch nicht bestätigt.", "Les dates sont estimées sur la base des années précédentes. Les dates officielles ne sont pas encore confirmées.", "Le date sono stimate sulla base degli anni precedenti. Le date ufficiali non sono ancora confermate.", "Data's zijn geschat op basis van voorgaande jaren. Officiële data zijn nog niet bevestigd.", "Datumi su procijenjeni na temelju prethodnih godina. Službeni datumi još nisu potvrđeni.",
        "Daty są szacowane na podstawie wcześniejszych lat. Oficjalne daty nie zostały jeszcze potwierdzone."
      ),
      officialSite: t5(lang, "Official site", "Offizielle Website", "Site officiel", "Sito ufficiale", "Officiële site", "Službena stranica",
        "Oficjalna strona"
      ),
      aboutDates: t5(lang, "About Festival Dates & Crowd Predictions", "Über Festivaldaten & Besucherprognosen", "À propos des dates de festivals et prévisions", "Informazioni sulle date dei festival e previsioni", "Over festivaldatums en bezoekersprognoses", "O datumima festivala i prognozama gužvi",
        "O datach festiwali i prognozach tłumów"
      ),
    },

    besttime: {
      title: t5(lang, "Best Time to Visit Croatia 2026 – Weather, Crowds & Prices", "Beste Reisezeit Kroatien 2026 – Wetter, Andrang & Preise", "Meilleur moment pour visiter la Croatie 2026 – Météo, foule et prix", "Periodo migliore per visitare la Croazia 2026 – Meteo, affluenza e prezzi", "Beste reistijd Kroatië 2026 – Weer, drukte & prijzen", "Najbolje vrijeme za posjet Hrvatskoj 2026 – Vremenska prognoza, gužve i cijene",
        "Najlepszy czas na odwiedzenie Chorwacji w 2026 roku – Pogoda, Tłumy i Ceny"
      ),
      tripPlanner: t5(lang, "Trip Planner", "Reiseplaner", "Planificateur de voyage", "Pianificatore di viaggio", "Reisplanner", "Planer putovanja",
        "Planer podróży"
      ),
      bestTimeTitle: t5(lang, "Best Time to Visit Croatia", "Beste Reisezeit für Kroatien", "Meilleur moment pour visiter la Croatie", "Periodo migliore per visitare la Croazia", "Beste reistijd voor Kroatië", "Najbolje vrijeme za posjet Hrvatskoj",
        "Najlepszy czas na odwiedzenie Chorwacji"
      ),
      croatiaOverall: t5(lang, "Croatia Overall", "Kroatien Gesamt", "Croatie en général", "Croazia in generale", "Kroatië algemeen", "Hrvatska općenito",
        "Chorwacja ogólnie"
      ),
      bestMonths: t5(lang, "Best months:", "Beste Monate:", "Meilleurs mois :", "Mesi migliori:", "Beste maanden:", "Najbolji mjeseci:",
        "Najlepsze miesiące:"
      ),
      aiConfidence: (n: number) => t5(lang, `${n}% AI confidence`, `${n}% KI-Konfidenz`, `${n}% confiance IA`, `${n}% fiducia IA`, `${n}% AI-vertrouwen`, `${n}% AI pouzdanost`,
        `${n}% pewności AI`
      ),
      ideal: t5(lang, "Ideal", "Ideal", "Idéal", "Ideale", "Ideaal", "Idealno",
        "Idealny"
      ),
      good: t5(lang, "Good", "Gut", "Bon", "Buono", "Goed", "Dobro",
        "Dobry"
      ),
      average: t5(lang, "Average", "Durchschnitt", "Moyen", "Medio", "Gemiddeld", "Prosječno",
        "Średni"
      ),
      busy: t5(lang, "Busy", "Belebt", "Fréquenté", "Affollato", "Druk", "Gužva",
        "Zatłoczony"
      ),
      crowdLevel: t5(lang, "Crowd Level", "Besucherandrang", "Niveau de foule", "Livello di affluenza", "Drukte", "Razina gužve",
        "Poziom tłumu"
      ),
      priceLevel: t5(lang, "Price Level", "Preisniveau", "Niveau de prix", "Livello dei prezzi", "Prijsniveau", "Razina cijena",
        "Poziom cen"
      ),
      aiConf: t5(lang, "AI Confidence", "KI-Konfidenz", "Confiance IA", "Fiducia IA", "AI-vertrouwen", "AI pouzdanost",
        "Pewność AI"
      ),
      dayTemp: t5(lang, "Day Temp", "Tagestemp.", "Temp. jour", "Temp. giorno", "Dagtemp.", "Dnevna temp.",
        "Temperatura dzienna"
      ),
      nightTemp: t5(lang, "Night Temp", "Nachttemp.", "Temp. nuit", "Temp. notte", "Nachttemp.", "Noćna temp.",
        "Temperatura nocna"
      ),
      seaTempLabel: t5(lang, "Sea Temp", "Meerestemp.", "Temp. mer", "Temp. mare", "Zeetemp.", "Temp. mora",
        "Temperatura morza"
      ),
      rainfall: t5(lang, "Rainfall", "Niederschlag", "Précipitations", "Precipitazioni", "Neerslag", "Oborine",
        "Opady"
      ),
      sunHoursLabel: t5(lang, "Sun hours", "Sonnenstunden", "Heures de soleil", "Ore di sole", "Zonuren", "Sunčani sati",
        "Godziny słońca"
      ),
      wind: t5(lang, "Wind", "Wind", "Vent", "Vento", "Wind", "Vjetar",
        "Wiatr"
      ),
      humidity: t5(lang, "Humidity", "Feuchtigkeit", "Humidité", "Umidità", "Vochtigheid", "Vlažnost",
        "Wilgotność"
      ),
      activity: t5(lang, "Activity", "Aktivität", "Activité", "Attività", "Activiteit", "Aktivnost",
        "Aktywność"
      ),
      crowds: t5(lang, "Crowds", "Andrang", "Foule", "Affluenza", "Drukte", "Gužve",
        "Tłumy"
      ),
      temperatureRainfall: t5(lang, "Temperature & Rainfall", "Temperatur & Regen", "Température et précipitations", "Temperatura e precipitazioni", "Temperatuur & Neerslag", "Temperatura i oborine",
        "Temperatura i opady"
      ),
      activityMatrix: t5(lang, "Activity Suitability Matrix", "Aktivitäts-Eignungs-Matrix", "Matrice d'aptitude aux activités", "Matrice di idoneità alle attività", "Activiteitsgeschiktheidsmatrix", "Matrica pogodnosti aktivnosti",
        "Macierz odpowiedniości aktywności"
      ),
      suitabilityDesc: t5(lang, "Suitability of months for various trip types (0–100%)", "Eignung der Monate für verschiedene Urlaubsarten (0–100%)", "Aptitude des mois pour différents types de voyage (0–100%)", "Idoneità dei mesi per vari tipi di viaggio (0–100%)", "Geschiktheid van maanden voor verschillende soorten reizen (0–100%)", "Pogodnost mjeseci za razne vrste putovanja (0–100%)",
        "Odpowiedniość miesięcy dla różnych typów podróży (0–100%)"
      ),
      summary: t5(lang, "Summary", "Zusammenfassung", "Résumé", "Riepilogo", "Samenvatting", "Sažetak",
        "Podsumowanie"
      ),
      priceLevels: t5(lang, "Price Levels", "Preisniveau", "Niveaux de prix", "Livelli di prezzo", "Prijsniveaus", "Razine cijena",
        "Poziomy cen"
      ),
      holidayCalendar: t5(lang, "Holiday Calendar & Crowd Impact", "Ferienkalender & Crowd-Impact", "Calendrier des vacances et impact de la foule", "Calendario delle vacanze e impatto affluenza", "Vakantiekalender & Drukte-impact", "Kalendar praznika i utjecaj na gužve",
        "Kalendarz wakacyjny i wpływ tłumów"
      ),
      marketPressure: t5(lang, "Market pressure index by month (0–100)", "Marktdruckindex nach Monat (0–100)", "Indice de pression du marché par mois (0–100)", "Indice di pressione del mercato per mese (0–100)", "Marktdrukindex per maand (0–100)", "Indeks tržišnog pritiska po mjesecu (0–100)",
        "Indeks presji rynkowej według miesiąca (0–100)"
      ),
      schoolHolidays: t5(lang, "School holidays by source market", "Schulferien nach Quellmarkt", "Vacances scolaires par marché source", "Vacanze scolastiche per mercato di origine", "Schoolvakanties per bronmarkt", "Školski praznici po tržištu porijekla",
        "Wakacje szkolne według rynku źródłowego"
      ),
      extremeLabel: t5(lang, "80+ Extreme", "80+ Extrem", "80+ Extrême", "80+ Estremo", "80+ Extreem", "80+ Ekstremno",
        "80+ Ekstremalne"
      ),
      highLabel: t5(lang, "50–79 High", "50–79 Hoch", "50–79 Élevé", "50–79 Alto", "50–79 Hoog", "50–79 Visoko",
        "50–79 Wysokie"
      ),
      moderateLabel: t5(lang, "25–49 Moderate", "25–49 Mäßig", "25–49 Modéré", "25–49 Moderato", "25–49 Matig", "25–49 Umjereno",
        "25–49 Umiarkowane"
      ),
      quietLabel: t5(lang, "0–24 Quiet", "0–24 Ruhig", "0–24 Calme", "0–24 Tranquillo", "0–24 Rustig", "0–24 Mirno",
        "0–24 Ciche"
      ),
      peakLabel: t5(lang, "Peak", "Peak", "Pic", "Picco", "Piek", "Vršno",
        "Szczyt"
      ),
      nightsPercent: (n: number) => t5(lang, `${n}% of nights`, `${n}% der Nächtigungen`, `${n}% des nuitées`, `${n}% dei pernottamenti`, `${n}% van de overnachtingen`, `${n}% noćenja`,
        `${n}% nocy`
      ),
      rainfallLabel: t5(lang, "Rainfall (mm)", "Niederschlag (mm)", "Précipitations (mm)", "Precipitazioni (mm)", "Neerslag (mm)", "Oborine (mm)",
        "Opady (mm)"
      ),
      maxTempLabel: t5(lang, "Max Temp (°C)", "Höchsttemperatur (°C)", "Temp. max (°C)", "Temp. max (°C)", "Max. temp. (°C)", "Maks. temp. (°C)",
        "Max temperatura (°C)"
      ),
      nightTempLabel: t5(lang, "Night Temp (°C)", "Nachttemperatur (°C)", "Temp. nuit (°C)", "Temp. notte (°C)", "Nachttemp. (°C)", "Noćna temp. (°C)",
        "Temperatura nocna (°C)"
      ),
      seaTempChartLabel: t5(lang, "Sea Temp (°C)", "Meerestemperatur (°C)", "Temp. mer (°C)", "Temp. mare (°C)", "Zeetemp. (°C)", "Temp. mora (°C)",
        "Temperatura morza (°C)"
      ),
      crowdPressure: t5(lang, "Crowd Pressure", "Besucherdruck", "Pression de foule", "Pressione della folla", "Bezoekersdruk", "Pritisak gužvi",
        "Presja tłumu"
      ),
      crowdLevelLabel: (_n: number) => t5(lang, "Crowd Level", "Besucherandrang", "Affluence", "Affluenza", "Drukte", "Razina gužve",
        "Poziom tłumu"
      ),
      months: {
        short: {
          en: ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"],
          de: ["JAN", "FEB", "MÄR", "APR", "MAI", "JUN", "JUL", "AUG", "SEP", "OKT", "NOV", "DEZ"],
          fr: ["JAN", "FÉV", "MAR", "AVR", "MAI", "JUN", "JUL", "AOÛ", "SEP", "OCT", "NOV", "DÉC"],
          it: ["GEN", "FEB", "MAR", "APR", "MAG", "GIU", "LUG", "AGO", "SET", "OTT", "NOV", "DIC"],
          nl: ["JAN", "FEB", "MRT", "APR", "MEI", "JUN", "JUL", "AUG", "SEP", "OKT", "NOV", "DEC"],
          hr: ["SIJ", "VEL", "OŽU", "TRA", "SVI", "LIP", "SRP", "KOL", "RUJ", "LIS", "STU", "PRO"],
          pl: ["STY", "LUT", "MAR", "KWI", "MAJ", "CZE", "LIP", "SIE", "WRZ", "PAŹ", "LIS", "GRU"],
        },
        full: {
          en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
          de: ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"],
          fr: ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"],
          it: ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"],
          nl: ["Januari", "Februari", "Maart", "April", "Mei", "Juni", "Juli", "Augustus", "September", "Oktober", "November", "December"],
          hr: ["Siječanj", "Veljača", "Ožujak", "Travanj", "Svibanj", "Lipanj", "Srpanj", "Kolovoz", "Rujan", "Listopad", "Studeni", "Prosinac"],
          pl: ["Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec", "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"],
        }
      }
    },
    favorites: {
      title: t5(lang, "My Favorite Croatian Destinations", "Meine Favoriten – Kroatische Reiseziele", "Mes destinations croates favorites", "Le mie destinazioni croate preferite", "Mijn favoriete Kroatische bestemmingen", "Moja omiljena hrvatska odredišta",
        "Moje ulubione chorwackie destynacje"
      ),
      description: t5(lang, "Save and organise your favourite places in Croatia.", "Speichern und organisieren Sie Ihre Lieblingsplätze in Kroatien.", "Sauvegardez et organisez vos lieux préférés en Croatie.", "Salva e organizza i tuoi luoghi preferiti in Croazia.", "Sla uw favoriete plaatsen in Kroatië op en organiseer ze.", "Spremi i organiziraj svoja omiljena mjesta u Hrvatskoj.",
        "Zapisz i zorganizuj swoje ulubione miejsca w Chorwacji."
      ),
      yourFavourites: t5(lang, "Your Favourites", "Meine Favoriten", "Vos favoris", "I tuoi preferiti", "Uw favorieten", "Vaši favoriti",
        "Twoje ulubione"
      ),
      notLoggedIn: t5(lang, "Sign in to save your favourite destinations and access them anywhere.", "Melden Sie sich an, um Ihre Lieblingsziele zu speichern und überall darauf zuzugreifen.", "Connectez-vous pour sauvegarder vos destinations préférées et y accéder n'importe où.", "Accedi per salvare le tue destinazioni preferite e accedervi ovunque.", "Log in om uw favoriete bestemmingen op te slaan en overal te openen.", "Prijavite se da biste spremili omiljena odredišta i pristupali im bilo gdje.",
        "Zaloguj się, aby zapisać swoje ulubione destynacje i uzyskać do nich dostęp z dowolnego miejsca."
      ),
      signIn: t5(lang, "Sign In", "Anmelden", "Se connecter", "Accedi", "Inloggen", "Prijava",
        "Zaloguj się"
      ),
      browseDestinations: t5(lang, "Browse Destinations", "Reiseziele erkunden", "Parcourir les destinations", "Sfoglia le destinazioni", "Bestemmingen bekijken", "Pregledaj odredišta",
        "Przeglądaj destynacje"
      ),
      myFavourites: t5(lang, "My Favourites", "Meine Favoriten", "Mes favoris", "I miei preferiti", "Mijn favorieten", "Moji favoriti",
        "Moje ulubione"
      ),
      noSavedYet: t5(lang, "No saved destinations yet", "Noch keine gespeicherten Reiseziele", "Aucune destination sauvegardée", "Nessuna destinazione salvata", "Nog geen opgeslagen bestemmingen", "Nema još spremljenih odredišta",
        "Brak zapisanych destynacji"
      ),
      signInToSave: t5(lang, "Sign in and start saving places you love.", "Melden Sie sich an und beginnen Sie, Ihre Lieblingsplätze zu speichern.", "Connectez-vous et commencez à sauvegarder les endroits que vous aimez.", "Accedi e inizia a salvare i luoghi che ami.", "Log in en begin met het opslaan van plaatsen die u leuk vindt.", "Prijavite se i počnite spremati mjesta koja volite.",
        "Zaloguj się i zacznij zapisywać miejsca, które kochasz."
      ),
      exploreDests: t5(lang, "Explore Destinations", "Reiseziele erkunden", "Explorer les destinations", "Esplora le destinazioni", "Bestemmingen verkennen", "Istražite odredišta",
        "Odkryj Miejsca"
      ),
    },

    compare: {
      title: t5(lang, "Compare Croatian Destinations", "Kroatische Reiseziele vergleichen", "Comparer les destinations croates", "Confronta le destinazioni croate", "Kroatische bestemmingen vergelijken", "Usporedi hrvatska odredišta",
        "Porównaj Miejsca w Chorwacji"
      ),
      description: t5(lang, "Compare Croatian destinations side by side – crowds, weather, prices and amenities.", "Kroatische Reiseziele im Vergleich – Besucherandrang, Wetter, Preise und Einrichtungen.", "Comparez les destinations croates côte à côte – fréquentation, météo, prix et équipements.", "Confronta le destinazioni croate fianco a fianco – affluenza, meteo, prezzi e servizi.", "Vergelijk Kroatische bestemmingen naast elkaar – drukte, weer, prijzen en faciliteiten.", "Usporedi hrvatska odredišta – gužve, vremenska prognoza, cijene i sadržaji.",
        "Porównaj miejsca w Chorwacji obok siebie – tłumy, pogoda, ceny i udogodnienia."
      ),
      nothingToCompare: t5(lang, "Nothing to compare", "Nichts zu vergleichen", "Rien à comparer", "Nulla da confrontare", "Niets te vergelijken", "Nema ničega za usporedbu",
        "Nic do porównania"
      ),
      addLocations: t5(lang, "Add locations from the home page to compare them here.", "Fügen Sie Orte von der Startseite hinzu, um sie hier zu vergleichen.", "Ajoutez des lieux depuis la page d'accueil pour les comparer ici.", "Aggiungi luoghi dalla home page per confrontarli qui.", "Voeg locaties toe vanaf de startpagina om ze hier te vergelijken.", "Dodajte mjesta s početne stranice kako biste ih usporedili ovdje.",
        "Dodaj lokalizacje z strony głównej, aby porównać je tutaj."
      ),
      browseLocations: t5(lang, "Browse Locations", "Orte erkunden", "Parcourir les lieux", "Sfoglia le posizioni", "Locaties bekijken", "Pregledaj mjesta",
        "Przeglądaj Miejsca"
      ),
      backToExplore: t5(lang, "Back to explore", "Zurück zur Übersicht", "Retour à l'exploration", "Torna all'esplorazione", "Terug naar verkennen", "Natrag na istraživanje",
        "Wróć do odkrywania"
      ),
      compareLocations: t5(lang, "Compare Locations", "Orte vergleichen", "Comparer les lieux", "Confronta le posizioni", "Locaties vergelijken", "Usporedi mjesta",
        "Porównaj Miejsca"
      ),
      clearAll: t5(lang, "Clear All", "Alle entfernen", "Tout effacer", "Cancella tutto", "Alles wissen", "Obriši sve",
        "Wyczyść Wszystko"
      ),
      bestMonths: t5(lang, "Best Months", "Beste Monate", "Meilleurs mois", "Mesi migliori", "Beste maanden", "Najbolji mjeseci",
        "Najlepsze Miesiące"
      ),
      parking: t5(lang, "Parking", "Parken", "Parking", "Parcheggio", "Parkeren", "Parkiranje",
        "Parking"
      ),
      yes: t5(lang, "Yes", "Ja", "Oui", "Sì", "Ja", "Da",
        "Tak"
      ),
      no: t5(lang, "No", "Nein", "Non", "No", "Nee", "Ne",
        "Nie"
      ),
      nearby: t5(lang, "Nearby", "In der Nähe", "À proximité", "Nelle vicinanze", "In de buurt", "U blizini",
        "W pobliżu"
      ),
      campingLabel: t5(lang, "Camping", "Camping", "Camping", "Campeggio", "Kamperen", "Kampiranje",
        "Kemping"
      ),
      viewDetails: t5(lang, "View Full Details", "Alle Details ansehen", "Voir tous les détails", "Vedi tutti i dettagli", "Bekijk alle details", "Prikaži sve detalje",
        "Zobacz Pełne Szczegóły"
      ),
      addAnother: t5(lang, "Add another location", "Weiteren Ort hinzufügen", "Ajouter un autre lieu", "Aggiungi un'altra posizione", "Voeg nog een locatie toe", "Dodaj još jedno mjesto",
        "Dodaj kolejną lokalizację"
      ),
      browseAll: t5(lang, "Browse All Destinations", "Alle Reiseziele erkunden", "Parcourir toutes les destinations", "Sfoglia tutte le destinazioni", "Alle bestemmingen bekijken", "Pregledaj sva odredišta",
        "Przeglądaj Wszystkie Miejsca"
      ),
      city: t5(lang, "City", "Stadt", "Ville", "Città", "Stad", "Grad",
        "Miasto"
      ),
      beach: t5(lang, "Beach", "Strand", "Plage", "Spiaggia", "Strand", "Plaža",
        "Plaża"
      ),
      nature: t5(lang, "Nature", "Natur", "Nature", "Natura", "Natuur", "Priroda",
        "Przyroda"
      ),
    },

    common: {
      loading: t5(lang, "Loading...", "Lädt...", "Chargement...", "Caricamento...", "Laden...", "Učitavam...",
        "Ładowanie..."
      ),
      error: t5(lang, "Error", "Fehler", "Erreur", "Errore", "Fout", "Greška",
        "Błąd"
      ),
      notFound: t5(lang, "Not found", "Nicht gefunden", "Introuvable", "Non trovato", "Niet gevonden", "Nije pronađeno",
        "Nie znaleziono"
      ),
      ad: t5(lang, "Ad", "Anzeige", "Pub", "Annuncio", "Advertentie", "Oglas",
        "Reklama"
      ),
      regionLabel: t5(lang, "Region", "Region", "Région", "Regione", "Regio", "Regija",
        "Region"
      ),
      day: t5(lang, "day", "Tag", "jour", "giorno", "dag", "dan",
        "dzień"
      ),
    },
  };
}
